import { defineStore } from 'pinia';
import moment from 'moment';
import { handleRequest } from '@/composables';
import { RequestMethodEnum } from '@/types/enums/RequestMethodEnum';
import type {
    ProductStatisticsStateType,
    ProductStatisticsGettersType,
    ProductStatisticsActionsType,
} from '@/types/stores/ProductStatisticsStoreTypes';
import type {
    ProductStatisticsFilterType,
    ProductStatisticsItemType,
    ProductStatisticsGroupType,
} from '@/types/statistics/ProductStatisticsType';
import type { FilterChipType } from '@/types/statistics/FilterChipType';
import type { ProductType } from '@/types/ProductType';
import { formatDateRangeChip } from '@/utils/storeUtils';
import { buildQueryString } from '@/utils/queryUtils';
import { getIntelligentChartColors } from '@/utils/chartColorUtils';
import { getLocalizedValue } from '@/utils/localizationUtils';
import i18n from '@/plugins/i18n';
import { VDataTableServer } from 'vuetify/components';
import type { LineChartDataType } from '@/types/ChartType';
import type { AxiosResponse } from 'axios';
import type { GroupingPeriodType } from '@/types/GroupingPeriodType';

const createDefaultFilters = (): ProductStatisticsFilterType => ({
    dateRange: {
        start: moment().subtract(7, 'days').startOf('day').toDate(),
        end: moment().endOf('day').toDate(),
    },
    groupedBy: 'day',
    warehouseNodeIds: undefined,
    productIds: undefined,
});

const createDefaultHeaders = (): VDataTableServer['$props']['headers'] => [
    {
        title: 'products.productId',
        key: 'product_id',
        sortable: true,
    },
    {
        title: 'common.name',
        key: 'name',
        sortable: true,
    },
    {
        title: 'statistics.products.table.price',
        key: 'prices',
        sortable: true,
    },
    {
        title: 'statistics.products.table.margin',
        key: 'margin',
        sortable: true,
    },
    {
        title: 'statistics.products.table.interactionTime',
        key: 'interaction_time',
        sortable: true,
    },
    {
        title: 'statistics.products.table.soldAmount',
        key: 'sold_amount',
        sortable: true,
    },
    {
        title: 'statistics.products.table.interactionCount',
        key: 'interaction_count',
        sortable: true,
    },
];

export const useProductStatisticsStore = defineStore<
    'productStatistics',
    ProductStatisticsStateType,
    ProductStatisticsGettersType,
    ProductStatisticsActionsType
>('productStatistics', {
    state: (): ProductStatisticsStateType => ({
        isLoading: false,
        filters: createDefaultFilters(),
        data: null,
        headers: createDefaultHeaders(),
        selectedMetrics: ['sold_amount', 'interaction_count'],
        allProducts: [],
    }),

    getters: {
        getFilteredData: state => state.data,
        hasData: state => !!state.data && state.data.items.length > 0,
        getFilterChips: (state): FilterChipType[] => {
            const chips: FilterChipType[] = [];
            const { t } = i18n.global;

            chips.push({
                key: 'groupedBy',
                label: `${t('common.groupedBy')}: ${t(`common.grouping.${state.filters.groupedBy}`)}`,
                value: state.filters.groupedBy,
                icon: 'mdi-group',
                removable: false,
            });

            if (state.filters.dateRange?.start && state.filters.dateRange?.end) {
                chips.push(formatDateRangeChip(state.filters.dateRange));
            }

            if (state.filters.productIds?.length && state.allProducts.length > 0) {
                const productNames = state.filters.productIds
                    .map(id => {
                        const product = state.allProducts.find(item => item.value === id);

                        return product?.title || `ID: ${id}`;
                    })
                    .filter(Boolean)
                    .join(', ');

                chips.push({
                    key: 'productIds',
                    label: `${t('statistics.filters.product')}: ${productNames}`,
                    value: state.filters.productIds,
                    icon: 'mdi-package-variant',
                    removable: true,
                });
            }

            if (state.filters.warehouseNodeIds?.length && state.data?.items) {
                const allNodes = new Map<number, string>();
                for (const item of state.data.items) {
                    if (item.warehouse_nodes) {
                        for (const node of item.warehouse_nodes) {
                            allNodes.set(node.id, node.name);
                        }
                    }
                }

                const nodeNames = state.filters.warehouseNodeIds
                    .map(id => allNodes.get(id))
                    .filter(Boolean)
                    .join(', ');

                chips.push({
                    key: 'warehouseNodeIds',
                    label: `${t('statistics.filters.warehouse')}: ${nodeNames}`,
                    value: state.filters.warehouseNodeIds,
                    icon: 'mdi-warehouse',
                    removable: true,
                });
            }

            return chips;
        },
        getTranslatedHeaders: state => {
            const { t } = i18n.global;

            return (
                state.headers?.map(header => ({
                    ...header,
                    title: header.title ? t(header.title) : header.title,
                })) || []
            );
        },

        getChartData: (state): LineChartDataType => {
            if (!state.data?.groups?.length) {
                return { labels: [], datasets: [] };
            }

            const labels = state.data.groups.map(group => group.label);
            const datasets = [];

            const metricConfig: Record<string, { label: string; color: string }> = {
                interaction_time: {
                    label: i18n.global.t('statistics.products.metrics.interactionTime'),
                    color: '#2196F3',
                },
                sold_amount: {
                    label: i18n.global.t('statistics.products.metrics.soldAmount'),
                    color: '#4CAF50',
                },
                interaction_count: {
                    label: i18n.global.t('statistics.products.metrics.interactionCount'),
                    color: '#FF9800',
                },
            };

            const colorPairs = getIntelligentChartColors(state.selectedMetrics.length);

            for (const [index, metric] of state.selectedMetrics.entries()) {
                const config = metricConfig[metric];
                if (!config) {
                    continue;
                }

                const colorPair = colorPairs[index];

                datasets.push({
                    label: config.label,
                    data: state.data.groups.map(group => group.data[metric as keyof typeof group.data]),
                    borderColor: colorPair.borderColor,
                    backgroundColor: colorPair.backgroundColor,
                    fill: false,
                    tension: 0.1,
                });
            }

            return { labels, datasets };
        },

        getWarehouseNodeOptions: state => {
            if (!state.data?.items) {
                return [];
            }

            const uniqueNodes = new Map<number, string>();

            for (const item of state.data.items) {
                if (item.warehouse_nodes) {
                    for (const node of item.warehouse_nodes) {
                        if (!uniqueNodes.has(node.id)) {
                            uniqueNodes.set(node.id, node.name);
                        }
                    }
                }
            }

            return Array.from(uniqueNodes.entries()).map(([id, name]) => ({
                title: name,
                value: id,
            }));
        },

        getProductOptions: state => state.allProducts,
    },

    actions: {
        generateMockStatistics(): Pick<
            ProductStatisticsItemType,
            'margin' | 'interaction_time' | 'sold_amount' | 'interaction_count'
            > {
            return {
                margin: Math.round((Math.random() * 40 + 5) * 100) / 100,
                interaction_time: Math.round(Math.random() * 290 + 10),
                sold_amount: Math.round(Math.random() * 50000),
                interaction_count: Math.round(Math.random() * 500),
            };
        },

        generateMockGroups(
            groupedBy: GroupingPeriodType,
            startDate: Date,
            endDate: Date,
        ): ProductStatisticsGroupType[] {
            const groups: ProductStatisticsGroupType[] = [];
            const start = moment(startDate);
            const end = moment(endDate);
            let current = start.clone();

            while (current.isSameOrBefore(end)) {
                let label = '';
                let nextPeriod = current.clone();

                switch (groupedBy) {
                case 'day':
                    label = current.format('DD.MM.YYYY');
                    nextPeriod = current.clone().add(1, 'day');
                    break;
                case 'week':
                    label = `${current.format('DD.MM')} - ${current.clone().add(6, 'days').format('DD.MM.YYYY')}`;
                    nextPeriod = current.clone().add(1, 'week');
                    break;
                case 'month':
                    label = current.format('MMMM YYYY');
                    nextPeriod = current.clone().add(1, 'month');
                    break;
                default:
                    label = current.format('DD.MM.YYYY');
                    nextPeriod = current.clone().add(1, 'day');
                }

                groups.push({
                    type: groupedBy,
                    label,
                    data: {
                        interaction_time: Math.round(Math.random() * 5000 + 1000),
                        sold_amount: Math.round(Math.random() * 500000 + 50000),
                        interaction_count: Math.round(Math.random() * 1000 + 100),
                    },
                });

                current = nextPeriod;
            }

            return groups;
        },

        async fetchData() {
            if (this.isLoading) {
                return;
            }
            this.isLoading = true;

            const params: Record<string, any> = {};

            // Build filters in backend expected format: {"id":[{"op":"in","value":[1,2,3]}]}
            const filters: Record<string, Array<{ op: string; value: any }>> = {};

            if (this.filters.productIds?.length) {
                filters.id = [{ op: 'in', value: this.filters.productIds }];
            }

            const hasFilters = Object.keys(filters).length > 0;

            if (hasFilters) {
                params.filters = filters;
            }

            const query = buildQueryString(params);
            const endpoint = query ? `/products?${query}` : '/products';

            await handleRequest({
                method: RequestMethodEnum.Get,
                endpoint,
                onSuccess: (
                    response: AxiosResponse<{
                        items: ProductType[];
                        total: number;
                        per_page: number;
                        current_page: number;
                        last_page: number;
                    }>,
                ) => {
                    const products = response.data.items ?? [];

                    // When no filters, populate allProducts for autocomplete
                    if (!hasFilters) {
                        this.allProducts = products.map(product => ({
                            title: getLocalizedValue(product.name, product.product_id),
                            value: product.id,
                        }));
                    }

                    const items: ProductStatisticsItemType[] = products.map(product => {
                        const mockStats = this.generateMockStatistics();

                        return {
                            ...product,
                            ...mockStats,
                        };
                    });

                    const groups =
                        this.filters.dateRange?.start && this.filters.dateRange?.end
                            ? this.generateMockGroups(
                                this.filters.groupedBy,
                                this.filters.dateRange.start,
                                this.filters.dateRange.end,
                            )
                            : [];

                    let totalSoldAmount = 0;
                    let totalInteractions = 0;
                    let totalInteractionTime = 0;
                    let totalMargin = 0;

                    for (const item of items) {
                        totalSoldAmount += item.sold_amount;
                        totalInteractions += item.interaction_count;
                        totalInteractionTime += item.interaction_time;
                        totalMargin += item.margin;
                    }

                    this.data = {
                        groupedBy: this.filters.groupedBy,
                        startDate: this.filters.dateRange?.start?.toISOString() || '',
                        endDate: this.filters.dateRange?.end?.toISOString() || '',
                        summary: {
                            totalProducts: response.data.total,
                            totalSoldAmount,
                            totalInteractions,
                            averageInteractionTime:
                                items.length > 0 ? Math.round(totalInteractionTime / items.length) : 0,
                            averageMargin: items.length > 0 ? Math.round((totalMargin / items.length) * 100) / 100 : 0,
                        },
                        groups,
                        items,
                        pagination: {
                            total: response.data.total,
                            perPage: response.data.per_page,
                            currentPage: response.data.current_page,
                            lastPage: response.data.last_page,
                        },
                    };
                },
            });

            this.isLoading = false;
        },

        setFilters(filters: Partial<ProductStatisticsFilterType>) {
            this.filters = { ...this.filters, ...filters };
        },

        setDateRange(from: Date, to: Date) {
            this.filters.dateRange = { start: from, end: to };
        },

        setGroupBy(groupBy: 'day' | 'week' | 'month') {
            this.filters.groupedBy = groupBy;
        },

        resetFilters() {
            this.filters = createDefaultFilters();
        },

        async removeFilter(filterKey: string) {
            const defaultFilters = createDefaultFilters();

            switch (filterKey) {
            case 'dateRange':
                this.filters.dateRange = defaultFilters.dateRange;
                break;
            case 'groupedBy':
                this.filters.groupedBy = defaultFilters.groupedBy;
                break;
            case 'warehouseNodeIds':
                this.filters.warehouseNodeIds = undefined;
                break;
            case 'productIds':
                this.filters.productIds = undefined;
                break;

            default:
                console.warn(`Unknown filter key: ${filterKey}`);

                return;
            }

            await this.fetchData();
        },
    },
});
